import React, { PureComponent } from 'react';

import Icon from 'components/icon';
import InputField from 'components/form/form-field-input';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { connect } from 'react-redux';
import reduxUtils from 'util/redux';
import { t, Trans } from 'translations';
import SearchPanelProductList from '../search-panel-product-list';
import SearchPanelFavoriteProductList from '../search-panel-favorite-product-list';
import SearchPanelCategoryList from '../search-panel-category-list';
import SearchPanelHeader from '../search-panel-header';
import { add as addEvent, remove as removeEvent } from 'bianco.events';
import { enter } from 'util/dom/handle-accessible-key-events';
import ActionTeaser from 'components/action-teaser';
import MigrosLoginButton from 'components/migros-login-button';

class SearchPanelMobile extends PureComponent {
    static propTypes = {
        categoriesTotal: PropTypes.number.isRequired,
        closeSearch: PropTypes.func.isRequired,
        commonCategoriesTotal: PropTypes.number.isRequired,
        favoriteProductsTotal: PropTypes.number.isRequired,
        hasCumulus: PropTypes.bool.isRequired,
        isActive: PropTypes.bool.isRequired,
        isAuthenticated: PropTypes.bool.isRequired,
        isSearching: PropTypes.bool.isRequired,
        productsTotal: PropTypes.number.isRequired,
        reset: PropTypes.func.isRequired,
        scrollbarWidth: PropTypes.number.isRequired,
        search: PropTypes.func.isRequired,
        setLimit: PropTypes.func.isRequired,
        unlockScroll: PropTypes.func.isRequired,
        setFuzzy: PropTypes.func.isRequired,
        persistTerm: PropTypes.func.isRequired,
        term: PropTypes.string.isRequired,
        endpoints: PropTypes.object.isRequired,

    };

    constructor(props) {
        super(props);
        this.state = {
            term: props.term,
        };
    }

    componentDidMount() {
        addEvent(document.body, 'touchmove', this.onScroll, { passive: true });
    }

    componentWillUnmount() {
        removeEvent(document.body, 'touchmove');
    }


    onClearAction = () => {
        this.props.reset();
    };

    /*
    * Event Handling
    */

    onInput = value => {
        const term = value;
        this.props.setLimit({ limit: 16 });
        this.setState({ term });
        this.props.search({ term });
        this.props.persistTerm({ term });
        this.refs.results.scrollTop = 0;
    };

    onKeyPress = e => {
        const isEnterPressed = enter(e);
        this.props.setFuzzy({ isFuzzy: !isEnterPressed });

        if (isEnterPressed) {
            const { search } = this.props;
            const { term } = this.state;
            search({ term });
        }
    };

    onScroll = () => {
        const el = document.activeElement;
        if (el.tagName === 'INPUT') {
            el.blur();
        }
    };

    onSearchPanelClick = () => {
        this.setState({ term: '' });
        this.props.persistTerm({ term: '' });
    };

    closePanel = () => {
        this.props.closeSearch();
        this.props.unlockScroll();
        this.setState({ term: '' });
        this.props.persistTerm({ term: '' });
    };

    /*
    * Helpers
    */

    hasInput = () => {
        return this.state.term.length > 0;
    };

    hasResults = () => {
        const { productsTotal, favoriteProductsTotal } = this.props;
        return productsTotal > 0 || favoriteProductsTotal > 0;
    };


    /*
    * Conditional Rendering
    */
    renderCategoryBlock = () => {
        const { commonCategoriesTotal, categoriesTotal } = this.props;
        let title = `${ t('search.common_categories_title') } ${ commonCategoriesTotal > 0
            ? `(${ commonCategoriesTotal })`
            : '' }`;

        if (this.hasInput()) {
            title = `${ t('search.query_result_categories_title') } ${ categoriesTotal > 0 ? `(${ categoriesTotal })` : '' }`;
        }
        return (
            <div className="search-panel-mobile--categories">
                <SearchPanelHeader title={ title }/>
                <SearchPanelCategoryList/>
            </div>
        );
    };

    renderProductBlock = () => {
        const { isAuthenticated, hasCumulus, endpoints } = this.props;

        if (!this.hasInput()) {
            if (!isAuthenticated) {
                return (
                    <div className="search-panel-mobile--action-teaser-wrapper">
                        <ActionTeaser
                            imagePath="img/login-teaser/img_rabbit.svg"
                            titleTranslationKey="mymigros.login_title"
                            descriptionTranslationKey="mymigros.login_body"
                        >
                            <MigrosLoginButton
                                inline={ true }
                                big={ true }
                                facet={ 'inline-block' }/>
                        </ActionTeaser>
                    </div>
                );
            }

            if (isAuthenticated && !hasCumulus) {
                return (
                    <div className="search-panel-mobile--action-teaser-wrapper">
                        <ActionTeaser
                            imagePath="img/cumulus-teaser/img_happy_products.svg"
                            titleTranslationKey="mymigros.cumulus_connect_title"
                            descriptionTranslationKey="mymigros.cumulus_connect_body"
                        >
                            <a className="btn btn__primary btn__teaser"
                                href={ endpoints.userConnectCumulus() }>
                                <Trans path={ 'mymigros.cumulus_connect_action' }/>
                            </a>
                        </ActionTeaser>
                    </div>
                );
            }

            if (isAuthenticated && hasCumulus) {
                return (
                    <div className="search-panel-mobile--products">
                        <SearchPanelFavoriteProductList/>
                    </div>
                );
            }
        }

        return (
            <div className="search-panel-mobile--products">
                <SearchPanelProductList/>
            </div>
        );
    };

    render() {
        let { term } = this.state;
        let { isActive } = this.props;
        return (
            <div
                className={ classnames('search-panel-mobile', {
                    'search-panel-mobile__is-active': isActive,
                    'search-panel-mobile__is-inactive': !isActive,
                    'search-panel-mobile__has-results': this.hasResults(),
                }) }
            >
                <div className="search-panel-mobile--header">
                    <div className="search-panel-mobile--back">
                        <button className="search-panel-mobile--back-button" onClick={ this.closePanel }>
                            <Icon symbol="arrow-back" size="medium"/>
                        </button>
                    </div>
                    <div className="search-panel-mobile--input">
                        <InputField
                            hasClearAction={ this.onClearAction }
                            placeholder={ t('search.placeholder') }
                            className={ 'search-panel-mobile--search-field' }
                            type="search"
                            value={ term }
                            onInput={ this.onInput }
                            onKeyPress={ this.onKeyPress }
                            // onFocus={this.onFocus}
                            // onBlur={this.onBlur}
                            variant={ 'search-mobile' }
                        />
                    </div>
                </div>
                <div className="search-panel-mobile--results" ref='results'>
                    <div onClick={ e => this.onSearchPanelClick(e) }>
                        { this.renderCategoryBlock() }
                    </div>
                    { this.renderProductBlock() }
                </div>
            </div>
        );
    }
}

export default connect(
    reduxUtils.mapStateToProps({
        categoriesTotal: 'search.categoriesTotal',
        commonCategoriesTotal: 'search.commonCategoriesTotal',
        favoriteProductsTotal: 'user.favoriteProductsTotal',
        hasCumulus: 'user.cumulus_connected',
        isAuthenticated: 'user.isAuthenticated',
        isMobile: 'viewport.isMobile',
        isScrollLocked: 'scroll.isLocked',
        isSearching: 'search.isSearching',
        productsTotal: 'search.productsTotal',
        scrollbarWidth: 'scroll.scrollbarWidth',
        rotationPersistedTerm: 'search.rotationPersistedTerm',
        term: 'search.term',
        endpoints: 'router.endpoints',
    }),
    reduxUtils.mapDispatchToProps({
        closeSearch: 'search.close',
        lockScroll: 'scroll.lock',
        openSearch: 'search.open',
        reset: 'search.reset',
        search: 'search.query',
        setLimit: 'search.setLimit',
        unlockScroll: 'scroll.unlock',
        setFuzzy: 'search.setFuzzy',
        persistTerm: 'search.persistTerm',
    }),
)(SearchPanelMobile);



// WEBPACK FOOTER //
// ./mcart/assets/src/js/app/components/main-header/search/search-panel-mobile/index.js